/**
 * API Event Emitter
 *
 * Lightweight pub/sub event system for the Gladly public API.
 * Safe for the bootstrap bundle (zero Zustand/React imports).
 *
 * Usage:
 *   const emitter = new ApiEventEmitter();
 *   emitter.addObserver('message:sent', 'obs_1', callback);
 *   emitter.emit('message:sent', [{ type: 'TEXT', text: 'hello' }]);
 *   emitter.removeObserver('message:sent', 'obs_1');
 */

import type { WidgetEventName } from '@/domain/widgetEvent';
import { WIDGET_EVENTS } from '@/domain/widgetEvent';
import { logger } from '@/lib/logger';

export type EventCallback = (...args: unknown[]) => void;

interface Observer {
  id: string;
  func: EventCallback;
}

export class ApiEventEmitter {
  private observers: Map<WidgetEventName, Observer[]>;

  constructor() {
    this.observers = new Map();
    for (const event of WIDGET_EVENTS) {
      this.observers.set(event, []);
    }
  }

  isValidEvent(eventName: string): eventName is WidgetEventName {
    return WIDGET_EVENTS.some((e) => e === eventName);
  }

  addObserver(eventName: WidgetEventName, id: string, func: EventCallback): void {
    const list = this.observers.get(eventName);
    if (!list) {
      throw new Error('invalid eventName: ' + eventName);
    }
    const existing = list.findIndex((o) => o.id === id);
    if (existing !== -1) {
      list[existing] = { id, func };
    } else {
      list.push({ id, func });
    }
  }

  removeObserver(eventName: WidgetEventName, id: string): void {
    const list = this.observers.get(eventName);
    if (!list) return;
    this.observers.set(
      eventName,
      list.filter((o) => o.id !== id),
    );
  }

  emit(eventName: WidgetEventName, ...payload: unknown[]): void {
    const list = this.observers.get(eventName);
    if (!list) return;
    for (const observer of list) {
      try {
        observer.func(...payload);
      } catch (e) {
        logger.error('[chat-v2] observer error:', e);
      }
    }
  }
}
